import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { HomeAnnouncement } from "@/pages/home/components/HomeAnnouncement.tsx";
import { HomeCustomizeModal } from "@/pages/home/components/HomeCustomizeModal.tsx";
import { useEsignRequestDeeplink } from "@/pages/esign/use-esign-request-deeplink.hook.ts";
import {
  homeWidgetDefaultOrder,
  homeWidgetRegistry,
  isHomeWidgetAvailable,
} from "@/pages/home/widgets/home-widget.registry.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useHomeLayout } from "@/queries/use-employee-settings.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { Button, FontAwesomeIcon, Page, Stack, useModals } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { ClockInOutButton } from "../employees/time-tracking/components/ClockInOutButton.tsx";

export function Home() {
  const { t } = useTranslation();
  const { data: employee } = useEmployeeMe();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const { openModal } = useModals();

  const getGreetingByTime = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      return t("common:good_morning");
    } else if (hour >= 12 && hour < 17) {
      return t("common:good_afternoon");
    } else if (hour >= 17 && hour < 22) {
      return t("common:good_evening");
    } else {
      return t("common:good_night");
    }
  };

  useTitle(t("core:home"));

  useEsignRequestDeeplink();

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.home.viewed);
  });

  const { data: homeLayout } = useHomeLayout();

  const widgetContext = { canIEdit, isFeatureEnabled };
  const visibleWidgetIds = homeLayout
    ? homeLayout.widgets
        .filter((widget) => !widget.hidden && isHomeWidgetAvailable(widget.id, widgetContext))
        .map((widget) => widget.id)
    : homeWidgetDefaultOrder.filter((id) => isHomeWidgetAvailable(id, widgetContext));

  return (
    <Page variant="opaque">
      <Page.Header title={employee && `${getGreetingByTime()}, ${employee.first_name}`}>
        <Stack direction={"horizontal"} gap={"sm"}>
          <ClockInOutButton />
          <Button
            variant={"secondary"}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-sliders"} />}
            onClick={() => openModal(HomeCustomizeModal)}
          >
            {t("core:customize_home_screen")}
          </Button>
        </Stack>
      </Page.Header>
      <Page.Content>
        {employee && (
          <Stack className={"container-lg mx-auto"}>
            <HomeAnnouncement />
            {visibleWidgetIds.map((id) => {
              const { component: Widget } = homeWidgetRegistry[id];
              return <Widget key={id} />;
            })}
            <Stack direction={"horizontal"} justify={"center"} className={"mt-14"}>
              <Button
                variant={"secondary"}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-gear"} />}
                onClick={() => openModal(HomeCustomizeModal)}
              >
                {t("core:configure_home_screen")}
              </Button>
            </Stack>
          </Stack>
        )}
      </Page.Content>
    </Page>
  );
}
